import React, { useState, useEffect } from 'react';
import { analyticsService, authService } from '../services/api';

function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const user = authService.getCurrentUser();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await analyticsService.getDashboard();
      if (response.success) {
        setDashboardData(response.data);
      } else {
        // Don't show error, just use fallback
        setDashboardData(null);
      }
    } catch (err) {
      // Don't show error, just use fallback
      console.error('Dashboard load error:', err);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <div style={{ color: '#666' }}>Loading dashboard...</div>
        </div>
      </div>
    );
  }

  // Fallback stats if API fails
  const getFallbackStats = () => {
    switch (user?.role) {
      case 'staff':
        return [
          { label: 'My Orders', value: '-', icon: '📦', color: '#667eea' },
          { label: 'Pending', value: '-', icon: '⏳', color: '#f59e0b' },
          { label: 'Approved', value: '-', icon: '✅', color: '#10b981' },
          { label: 'This Week', value: '-', icon: '📅', color: '#8b5cf6' },
        ];
      case 'senior_nurse':
        return [
          { label: 'Pending Approvals', value: '-', icon: '✅', color: '#667eea' },
          { label: 'Products This Week', value: '-', icon: '📦', color: '#10b981' },
          { label: 'Total Items', value: '-', icon: '📊', color: '#f59e0b' },
          { label: 'Alerts', value: '-', icon: '⚠️', color: '#ef4444' },
        ];
      default:
        return [
          { label: 'Pending Orders', value: '-', icon: '📦', color: '#667eea' },
          { label: 'Total Products', value: '-', icon: '📋', color: '#10b981' },
          { label: 'Active Users', value: '-', icon: '👥', color: '#8b5cf6' },
          { label: 'Low Stock Items', value: '-', icon: '⚠️', color: '#ef4444' },
        ];
    }
  };

  const getStatsForRole = () => {
    if (!dashboardData) return getFallbackStats();

    switch (user?.role) {
      case 'staff':
        return dashboardData.staff_dashboard?.order_stats
          ? [
              { label: 'My Orders', value: dashboardData.staff_dashboard.order_stats.total_orders || 0, icon: '📦', color: '#667eea' },
              { label: 'Pending', value: dashboardData.staff_dashboard.order_stats.pending || 0, icon: '⏳', color: '#f59e0b' },
              { label: 'Approved', value: dashboardData.staff_dashboard.order_stats.approved || 0, icon: '✅', color: '#10b981' },
              { label: 'This Week', value: dashboardData.staff_dashboard.current_week?.current_week_orders || 0, icon: '📅', color: '#8b5cf6' },
            ]
          : getFallbackStats();

      case 'senior_nurse':
        return dashboardData.senior_nurse_dashboard
          ? [
              { label: 'Pending Approvals', value: dashboardData.senior_nurse_dashboard.pending_approvals?.pending_approvals || 0, icon: '✅', color: '#667eea' },
              { label: 'Products This Week', value: dashboardData.senior_nurse_dashboard.weekly_summary?.products_count || 0, icon: '📦', color: '#10b981' },
              { label: 'Total Items', value: dashboardData.senior_nurse_dashboard.weekly_summary?.total_quantity || 0, icon: '📊', color: '#f59e0b' },
              { label: 'Alerts', value: dashboardData.senior_nurse_dashboard.alerts?.length || 0, icon: '⚠️', color: '#ef4444' },
            ]
          : getFallbackStats();

      default:
        return dashboardData.admin_dashboard
          ? [
              { label: 'Pending Orders', value: dashboardData.admin_dashboard.pending_orders?.pending_orders || 0, icon: '📦', color: '#667eea' },
              { label: 'Total Products', value: dashboardData.admin_dashboard.products_stats?.total_products || 0, icon: '📋', color: '#10b981' },
              { label: 'Active Users', value: dashboardData.admin_dashboard.users_stats?.total_users || 0, icon: '👥', color: '#8b5cf6' },
              { label: 'Low Stock Items', value: dashboardData.admin_dashboard.low_stock_products?.length || 0, icon: '⚠️', color: '#ef4444' },
            ]
          : getFallbackStats();
    }
  };

  const stats = getStatsForRole();

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Welcome Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '30px',
        borderRadius: '16px',
        color: 'white',
        marginBottom: '30px',
        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
      }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '10px' }}>
          Welcome back, {user?.full_name}! 👋
        </h1>
        <p style={{ fontSize: '16px', opacity: 0.9 }}>
          {user?.role === 'staff' && 'Place orders and track your requests'}
          {user?.role === 'senior_nurse' && 'Review and approve weekly orders'}
          {(user?.role === 'super_admin' || user?.role === 'super_super_admin') && 'Manage your ward stock system'}
        </p>
      </div>

      {/* Analytics unavailable notice */}
      {!dashboardData && (
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          border: '2px solid #fbbf24',
          padding: '16px 20px',
          borderRadius: '12px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '24px' }}>ℹ️</span>
          <div>
            <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
              Analytics Data Unavailable
            </div>
            <div style={{ fontSize: '14px', color: '#78350f' }}>
              Dashboard statistics are currently unavailable. You can still use all other features normally.
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        {stats.map((stat, index) => (
          <div key={index} style={{
            background: 'white',
            padding: '25px',
            borderRadius: '16px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            transition: 'transform 0.3s, box-shadow 0.3s',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)';
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
              <div style={{
                width: '50px',
                height: '50px',
                background: `${stat.color}20`,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px'
              }}>
                {stat.icon}
              </div>
            </div>
            <div style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>
              {stat.label}
            </div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#333' }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '16px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        marginBottom: '30px'
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', color: '#333' }}>
          ⚡ Quick Actions
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          {user?.role === 'staff' && (
            <>
              <a href="/products" style={{ textDecoration: 'none' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <span style={{ fontSize: '28px' }}>🛒</span>
                  <span style={{ fontWeight: '600' }}>Place Order</span>
                </div>
              </a>
              <a href="/my-orders" style={{ textDecoration: 'none' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <span style={{ fontSize: '28px' }}>📋</span>
                  <span style={{ fontWeight: '600' }}>My Orders</span>
                </div>
              </a>
              <a href="/request-product" style={{ textDecoration: 'none' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <span style={{ fontSize: '28px' }}>📦</span>
                  <span style={{ fontWeight: '600' }}>Request Product</span>
                </div>
              </a>
            </>
          )}
          
          {(user?.role === 'super_admin' || user?.role === 'super_super_admin') && (
            <>
              <a href="/products" style={{ textDecoration: 'none' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <span style={{ fontSize: '28px' }}>📦</span>
                  <span style={{ fontWeight: '600' }}>Manage Products</span>
                </div>
              </a>
              <a href="/users" style={{ textDecoration: 'none' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <span style={{ fontSize: '28px' }}>👥</span>
                  <span style={{ fontWeight: '600' }}>Manage Users</span>
                </div>
              </a>
              <a href="/settings" style={{ textDecoration: 'none' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <span style={{ fontSize: '28px' }}>⚙️</span>
                  <span style={{ fontWeight: '600' }}>Settings</span>
                </div>
              </a>
            </>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '16px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', color: '#333' }}>
          📋 Recent Activity
        </h2>
        
        {dashboardData?.admin_dashboard?.recent_activity && dashboardData.admin_dashboard.recent_activity.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {dashboardData.admin_dashboard.recent_activity.slice(0, 5).map((activity, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '15px',
                background: '#f9f9f9',
                borderRadius: '12px',
                borderLeft: '4px solid #667eea'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: '#667eea',
                  borderRadius: '50%'
                }}></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#333', marginBottom: '5px' }}>
                    {activity.full_name || 'System'}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {activity.action}
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  {new Date(activity.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔭</div>
            <div>No recent activity available</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
